import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container, Table } from "reactstrap";

import { Heading } from "components/Heading";
import { ToggleInput } from "components/ToggleInput";
import { SearchInput } from "components/SearchInput";
import { Pagination } from "components/Pagination";
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest";
import { PageHeadings, PageTitles } from "../../constants";
import { customerListingApi, setInboxAccess, setMcapAccess } from "../../api/api.service";

// Two independent grants, toggled the same way. Inbox access is its own switch
// because the WhatsApp inbox shows every lead's phone number and messages, and
// MCAP access can be given to teacher accounts.
const GRANTS = {
  mcapAccess: { label: "MCAP access", save: setMcapAccess },
  inboxAccess: { label: "Inbox access", save: setInboxAccess },
};

/**
 * Who can open the standalone MCAP dashboard.
 *
 * MCAP moved to its own app, its own origin, its own container — the sidebar
 * no longer links to it at all. That app gates entry on `User.mcapAccess`,
 * checked server-side by every MCAP analytics endpoint (McapAccessGuard) and
 * re-checked client-side right after sign-in. This screen is the only place
 * that flag is set.
 *
 * It searches every account type, not just admins. MCAP started admin-only,
 * so this listed `type: ADMIN` with no search — workable while there were
 * three admins, useless for finding one teacher among thousands of accounts.
 * The backend now admits UserType.USER on all 23 MCAP analytics endpoints, so
 * a granted teacher or student sees the same org-wide MCAP an admin does, and
 * this screen has to be able to find them: hence `allTypes` plus the server's
 * own search over name, email and phone.
 *
 * Same flat privilege model as the rest of this console: any signed-in admin
 * can reach this screen. It does not add a new hole — granting MCAP is still
 * enforced server-side — but "who can grant MCAP" is not itself restricted.
 */
const AdminAccess = () => {
  const [updatingId, setUpdatingId] = useState(null);

  // Held apart from the fetched rows so a toggle needs no refetch (which would
  // flash the table and drop the reader's place). Cleared whenever a new result
  // set lands, so search and paging always show server truth.
  const [overrides, setOverrides] = useState({});

  const {
    page,
    data,
    total,
    pageSize,
    isFetching,
    searchText,
    handleSearchChange,
    handlePageClick,
    onChangePageSize,
  } = useLocalPaginatedRequest(customerListingApi, { allTypes: true }, []);

  useEffect(() => {
    setOverrides({});
  }, [data]);

  // The hook seeds `data` with "" before the first response lands, so this is
  // a type guard, not defensiveness for its own sake.
  const accounts = Array.isArray(data) ? data : [];

  const accessOf = (account, grant) => {
    const key = `${account.id}:${grant}`;
    return overrides[key] !== undefined ? overrides[key] : !!account[grant];
  };

  const toggle = async (id, grant) => {
    const target = accounts.find((a) => a.id === id);
    if (!target) return;

    const next = !accessOf(target, grant);
    const { label, save } = GRANTS[grant];

    setUpdatingId(`${id}:${grant}`);
    try {
      await save({ id, [grant]: next });
      setOverrides((prev) => ({ ...prev, [`${id}:${grant}`]: next }));
      toast.success(next ? `${label} granted.` : `${label} revoked.`);
    } catch (e) {
      // The switch stays where it was: the override is only written after the
      // call succeeds, so a failed grant cannot leave the row claiming access
      // the server never gave.
      toast.error(`Could not update ${label}.`);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="page-content">
      <Helmet>
        <title>{PageTitles.ADMIN_ACCESS}</title>
      </Helmet>
      <Container fluid>
        <Heading heading={PageHeadings.ADMIN_ACCESS} />
        <p className="text-muted mb-4">
          Search any account by name or email and switch MCAP on for them. They can open it the next time
          they sign in there — off takes effect immediately, everywhere. A granted account sees the same
          org-wide MCAP an admin does, whatever its type.
        </p>
        <p className="text-muted mb-4">
          Admin accounts always have both, so their rows show no switch. One email can hold an admin account
          and a separate teacher (USER) account; the switch on a USER row changes only that teacher account.
        </p>
        <p className="text-muted mb-4">
          Inbox access is separate: it opens the WhatsApp inbox in MCAP, where the team watches the AI&apos;s chats
          with teachers and schools, takes over and replies. It shows every lead&apos;s phone number and messages, so
          give it only to the people who handle those conversations. It works without MCAP access.
        </p>

        <div className="mb-3">
          <SearchInput
            placeholder="Search by name or email"
            value={searchText}
            onChange={handleSearchChange}
          />
        </div>

        <div className="table-responsive">
          <Table borderless className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th style={{ width: 120 }}>Type</th>
                <th style={{ width: 140 }}>MCAP access</th>
                <th style={{ width: 140 }}>Inbox access</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    Loading…
                  </td>
                </tr>
              )}
              {!isFetching && accounts.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    {searchText ? `No account matches “${searchText}”.` : "No accounts found."}
                  </td>
                </tr>
              )}
              {!isFetching &&
                accounts.map((account) => (
                  <tr key={account.id}>
                    <td>{account.name || "-"}</td>
                    <td>{account.email || "-"}</td>
                    <td>{account.type || "-"}</td>
                    {Object.keys(GRANTS).map((grant) => (
                      <td key={grant} data-grant={grant}>
                        {account.type === "ADMIN" ? (
                          // The server grants both to every admin account
                          // (AuthService.ApplyAdminGrants), so a switch here
                          // would only pretend to change something.
                          <span className="text-muted" title="Admin accounts always have this access">
                            Always on (admin)
                          </span>
                        ) : (
                          <ToggleInput
                            userId={account.id}
                            isChecked={accessOf(account, grant)}
                            isLoading={updatingId === `${account.id}:${grant}`}
                            toggleHandler={(id) => toggle(id, grant)}
                            trueContent="On"
                            falseContent="Off"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        {!!total && (
          <Pagination
            currentPage={page}
            totalData={total}
            rowLimit={pageSize}
            onPageClick={handlePageClick}
            handleChange={onChangePageSize}
          />
        )}
      </Container>
    </div>
  );
};

export default AdminAccess;
